import {
    GetInstagramAudioListStatsOrderingKeys,
    InstagramAudio,
    InstagramAudioListStats,
    InstagramUser,
} from '@round/api';
import WrapperPaginationTable, {
    WrapperPaginationTableProps,
} from '../../../../../ui/DataDisplay/WrapperTable/WrapperPaginationTable/WrapperPaginationTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Skeleton } from '@round/ui-kit';
import styles from './InstagramAudioExploreTable.module.css';
import AudioPlaybackThumbnail from '../../../../../ui/AudioPlaybackThumbnail/AudioPlaybackThumbnail';
import { AudioPlayerProvider } from '../../../../AudioPlayer/AudioPlayerContext';
import TruncatedTextCell from '../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../helpers';
import cn from 'classnames';
import BarsSmallChart from '../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import { buildInstagramAudioUrl } from '../../../helpers';

type InstagramAudioExploreTableRow = InstagramAudioListStats & {
    audio: InstagramAudio | null;
    user: InstagramUser | null;
};

type Props = Omit<
    WrapperPaginationTableProps<InstagramAudioExploreTableRow>,
    'columns' | 'columnPinning' | 'className'
> & {
    selectedOrdering: GetInstagramAudioListStatsOrderingKeys;
};
type Context<Key extends keyof InstagramAudioExploreTableRow> = CellContext<
    InstagramAudioExploreTableRow,
    InstagramAudioExploreTableRow[Key]
>;

const formatVideoCountgrowthBadgeValue = (ordering: GetInstagramAudioListStatsOrderingKeys, value: number) => {
    if (ordering.includes('relative')) {
        return `${roundTo2Dp(value * 100)}%`;
    }

    return formatNumberToKNotation(value);
};

const InstagramAudioExploreTable = ({ ...props }: Props) => {
    const columns: ColumnDef<InstagramAudioExploreTableRow, any>[] = [
        {
            header: 'Sound',
            id: 'audio',
            cell: ({ row: { original } }) => {
                if (props.isLoading) {
                    return <Skeleton />;
                }

                if (!original.audio) {
                    return '-';
                }

                const audioUrl = original.audio.audio_file_url ?? '';
                const imageUrl = original.audio.thumbnail_url ?? '';
                const instagramAudioUrl = buildInstagramAudioUrl(original.audio.instagram_id);

                return (
                    <div className={styles.audioBadge}>
                        <AudioPlaybackThumbnail className={styles.audioCover} audioUrl={audioUrl} imageUrl={imageUrl} />
                        <a
                            className={styles.cellValue}
                            href={instagramAudioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {original.audio.title}
                        </a>
                    </div>
                );
            },
        },
        {
            header: 'Author',
            id: 'user',
            cell: ({ row: { original } }) => {
                if (props.isLoading) {
                    return <Skeleton />;
                }

                const name = original.user?.username;

                if (!name) {
                    return '-';
                }

                return <TruncatedTextCell className={styles.cellValue}>{name}</TruncatedTextCell>;
            },
        },
        {
            header: 'Reels',
            accessorKey: 'reels_count',
            cell: ({ getValue, row: { original } }: Context<'reels_count'>) => {
                if (props.isLoading) {
                    return <Skeleton />;
                }
                const reelsCountValue = getValue();
                const reelsCountDisplayValue = reelsCountValue ? formatNumberToKNotation(reelsCountValue) : '-';

                const orderByValue = props.selectedOrdering.slice(1);
                const growthBadgeValue =
                    orderByValue !== 'reels_count' ? original[orderByValue as keyof InstagramAudioListStats] : null;

                return (
                    <>
                        <span className={styles.cellValue}>{reelsCountDisplayValue}</span>
                        {typeof growthBadgeValue === 'number' && (
                            <span
                                className={cn(styles.growthBadge, {
                                    [styles.increasing]: growthBadgeValue >= 0,
                                    [styles.decreasing]: growthBadgeValue < 0,
                                })}
                            >
                                {growthBadgeValue > 0 && '+'}
                                {formatVideoCountgrowthBadgeValue(props.selectedOrdering, growthBadgeValue)}
                            </span>
                        )}
                    </>
                );
            },
        },
        {
            header: 'Growth',
            accessorKey: 'reels_count_time_series',
            cell: ({ getValue }: Context<'reels_count_time_series'>) => {
                if (props.isLoading) {
                    return <Skeleton />;
                }

                const chartPoints = getValue()
                    .map((point) => ({ timestamp: point.time, value: point.value }))
                    .reverse()
                    .slice(-7);

                return (
                    <div className={styles.dailyGrowth}>
                        <BarsSmallChart
                            values={chartPoints}
                            color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <AudioPlayerProvider>
            <WrapperPaginationTable
                columns={columns}
                {...props}
                noDataLabel="No audios found"
                className={styles.table}
            />
        </AudioPlayerProvider>
    );
};

export default InstagramAudioExploreTable;
